// Alignment options for captions
export enum Alignment {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

// Font variant options
export enum FontVariants {
  REGULAR = 'regular',
  BOLD = 'bold',
  ITALIC = 'italic',
}

// Text capitalization options
export enum TextCapitalization {
  REGULAR = 'regular',
  UPPERCASE = 'uppercase',
  LOWERCASE = 'lowercase',
}

// Animation type options for captions
export enum AnimationKind {
  FADE_IN = 'fade_in',
  SLIDE_IN = 'slide_in',
  POP_IN = 'pop_in',
  WORD_BY_WORD = 'word_by_word',
  KARAOKE_HIGHLIGHT = 'karaoke_highlight',
}

// Word selection strategy for animations
export enum WordSelectionStrategy {
  PULSE = 'pulse',
  FILL = 'fill',
}

// Background animation type
export enum BackgroundAnimationKind {
  BOX = 'box',
  SMOOTH = 'smooth',
}

// Easing type options
export enum Easing {
  LINEAR = 'linear',
  EASE_IN = 'easeIn',
  EASE_OUT = 'easeOut',
  EASE_IN_OUT = 'easeInOut',
}

// Vertical position options for captions (kept for backwards compatibility)
export enum VerticalPosition {
  TOP = 'top',
  CENTER = 'center',
  BOTTOM = 'bottom',
}

// Selected font
export interface SelectedFont {
  fontId: string;
  variant: FontVariants;
  fontFamily?: string | null;
}

// Font settings
export interface FontSettings {
  size: number;
  selectedFont: SelectedFont;
}

// Color settings
export interface Color {
  hex: string;
  opacity: number;
}

// Spacing settings
export interface Spacing {
  lineHeight: number;
  kerning: number;
}

// Typography settings
export interface Typography {
  fontSettings: FontSettings;
  textColor: Color;
  textAlignment: Alignment;
  textCapitalization: TextCapitalization;
  captionLines: number;
  spacing: Spacing | null;
}

// Text stroke settings
export interface TextStroke {
  width: number;
  color: Color;
}

// Drop shadow settings
export interface DropShadow {
  color: Color;
  offsetX: number;
  offsetY: number;
  blur: number;
}

// Animation settings
export interface Animation {
  type: AnimationKind;
  duration?: number | null;
  wordSelectionStrategy?: WordSelectionStrategy | null;
  wordColor?: Color | null;
  wordMaxScaling?: number | null;
  background?: Background | null;
  backgroundAnimationType?: BackgroundAnimationKind | null;
  easing?: Easing | null;
}
export interface Background {
  cornerRadius?: number | null;
  color: Color;
  verticalPadding?: number | null;
  horizontalPadding?: number | null;
}
export interface Styling {
  textStroke?: TextStroke | null;
  dropShadow?: DropShadow | null;
  background?: Background | null;
}
// Caption style configuration
export interface StyleConfig {
  typography: Typography;
  styling: Styling;
  animation: Animation | null;
  normalizedVerticalPosition: number; // 0 is top
}

export const defaultStyle: StyleConfig = {
  typography: {
    fontSettings: {
      size: 30,
      selectedFont: {
        fontId: 'Roboto',
        variant: FontVariants.REGULAR,
      },
    },
    textColor: {
      hex: '#FFFFFF',
      opacity: 1,
    },
    textAlignment: Alignment.CENTER,
    textCapitalization: TextCapitalization.REGULAR,
    captionLines: 1,
    spacing: {
      lineHeight: 1.0,
      kerning: 0,
    },
  } as Typography,
  styling: {
    textStroke: {
      width: 0,
      color: {
        hex: '#000000',
        opacity: 1,
      },
    },
  } as Styling,
  normalizedVerticalPosition: 0.8,
  animation: null,
};

// Word timing interface
export interface Word {
  text: string;
  start: number;
  end: number;
}

// Caption interface
export interface Caption {
  id: string;
  start: number;
  end: number;
  text: string;
  wordTimings?: Word[];
}

export interface CaptionsData {
  captions: Caption[];
  languageCode: string;
}

// Layout types for caption rendering
export interface LineLayout {
  wordCount: number;
  wordOrder: number[];
  lineWidth: number;
  wordWidths: number[];
}

export interface CaptionLayout {
  lineLayouts: LineLayout[];
  spaceWidth: number;
}

// Caption style preset from API
export interface CaptionsStylePreset {
  id: string;
  name: string;
  description: string;
  captionsStyle: StyleConfig;
}
